/**
 * Map object clustering (issue #434).
 *
 * At low zoom the planner and fleet maps draw every waypoint, track and drone
 * at full size, so nearby objects pile into an illegible blob. Objects that
 * would overlap on screen are collapsed into one badge carrying their count.
 *
 * The maths is deliberately pure — it takes an already-projected pixel
 * position per item — so it can be reasoned about and tested without a map.
 */

/** A point to be clustered, with whatever the caller wants to carry along. */
export interface ClusterInput<T> {
  /** Stable identity, used to keep React keys stable across zooms. */
  id: string;
  lat: number;
  lng: number;
  /** Screen position at the current zoom, in absolute world pixels. */
  x: number;
  y: number;
  item: T;
}

/** One or more inputs that share a spot on screen. */
export interface Cluster<T> {
  /** Stable key: the id of the first member, which never changes for a lone item. */
  key: string;
  /** Centroid of the members, where the badge sits. */
  lat: number;
  lng: number;
  items: T[];
  /** Bounds of the members, for zoom-to-cluster. */
  bounds: [[number, number], [number, number]];
}

/**
 * Group inputs whose screen positions fall within `radiusPx` of a member.
 *
 * A greedy sweep in input order rather than a pixel grid: a grid splits two
 * points that straddle a cell boundary however close they are, which shows up
 * as a cluster that refuses to form. Iteration order is the caller's (route
 * order for waypoints), so the result is deterministic for a given zoom.
 *
 * O(n²) worst case, run only when the zoom changes; routes are hundreds of
 * points, not millions.
 */
export const clusterByPixelRadius = <T>(
  inputs: ReadonlyArray<ClusterInput<T>>,
  radiusPx: number
): Cluster<T>[] => {
  const clusters: Cluster<T>[] = [];
  const taken = new Array<boolean>(inputs.length).fill(false);
  const radiusSq = radiusPx * radiusPx;

  for (let i = 0; i < inputs.length; i += 1) {
    if (taken[i]) continue;
    const seed = inputs[i];
    taken[i] = true;
    const members = [seed];

    for (let j = i + 1; j < inputs.length; j += 1) {
      if (taken[j]) continue;
      const dx = inputs[j].x - seed.x;
      const dy = inputs[j].y - seed.y;
      if (dx * dx + dy * dy <= radiusSq) {
        taken[j] = true;
        members.push(inputs[j]);
      }
    }

    let latSum = 0;
    let lngSum = 0;
    let minLat = members[0].lat;
    let maxLat = members[0].lat;
    let minLng = members[0].lng;
    let maxLng = members[0].lng;
    for (const m of members) {
      latSum += m.lat;
      lngSum += m.lng;
      minLat = Math.min(minLat, m.lat);
      maxLat = Math.max(maxLat, m.lat);
      minLng = Math.min(minLng, m.lng);
      maxLng = Math.max(maxLng, m.lng);
    }

    clusters.push({
      key: seed.id,
      lat: latSum / members.length,
      lng: lngSum / members.length,
      items: members.map((m) => m.item),
      bounds: [
        [minLat, minLng],
        [maxLat, maxLng],
      ],
    });
  }

  return clusters;
};

/** What a cluster badge stands for. Drives its colour, glyph and label. */
export type ClusterKind = 'waypoints' | 'tracks' | 'drones';

/**
 * Badge appearance per kind. Colours come from the CVD-safe qualitative
 * palette the waypoint ramp already draws on (#341) and are deliberately far
 * apart in both hue and lightness. Colour is never the only cue: every badge
 * also carries a glyph, its count, and an accessible label.
 */
export const CLUSTER_STYLE: Record<
  ClusterKind,
  { background: string; foreground: string; glyph: string; noun: string; round: boolean }
> = {
  waypoints: {
    background: '#332288',
    foreground: '#ffffff',
    glyph: '•',
    noun: 'waypoints',
    round: true,
  },
  tracks: {
    background: '#44aa99',
    foreground: '#000000',
    glyph: '⤳',
    noun: 'flight tracks',
    round: false,
  },
  drones: {
    background: '#ddcc77',
    foreground: '#000000',
    glyph: '✈',
    noun: 'drones',
    round: true,
  },
};

/**
 * Badge diameter for a member count, in pixels. Bucketed rather than
 * continuous so a 3-member and a 300-member badge are obviously different
 * without the biggest ones swallowing the map.
 */
export const clusterBadgeSize = (count: number): number => {
  if (count < 10) return 32;
  if (count < 50) return 40;
  if (count < 200) return 48;
  return 56;
};
